import { IfPermission } from '@app/core/auth/authorization/components/IfPermission';
import { contentAi } from '@mi-front/api-clients';
import { Alert, Box } from '@mui/material';
import { Link } from 'react-router-dom';
import { FormattedMessage } from 'react-intl';
import { ContentAiFragmentDeliveryInfo } from './ContentAiFragmentDeliveryInfo';
import { ContentAiFragmentInfo } from './types';

/**
 * Props for the ContentAiFragmentOriginInfo component
 */
interface Props {
  /** The content object containing metadata about the AI-generated content */
  content: contentAi.ContentDto;
  /** The fragment information containing details about the source fragment */
  fragment: ContentAiFragmentInfo;
}

/**
 * ContentAiFragmentOriginInfo Component
 *
 * Displays information about the origin of AI-generated content, showing that it was
 * created from a specific fragment. This component renders an informational alert
 * that includes:
 *
 * - A link to the original fragment that was used as the source
 * - Conditional delivery information (if user has appropriate permissions)
 *
 * The component is typically used in content AI workflows to provide transparency
 * about the source material used for AI content generation.
 *
 * @example
 * ```tsx
 * <ContentAiFragmentOriginInfo
 *   content={contentData}
 *   fragment={fragmentInfo}
 * />
 * ```
 *
 * @param props - The component props
 * @param props.content - The content object containing metadata about the AI-generated content
 * @param props.fragment - The fragment information containing details about the source fragment
 *
 * @returns A React component that displays fragment origin information in an alert format
 */
export function ContentAiFragmentOriginInfo({ fragment, content }: Props) {
  return (
    <Alert
      severity="info"
      sx={{ fontSize: 'medium', mb: 3, '.MuiAlert-message': { width: '100%' } }}
    >
      <Box>
        <Box>
          <FormattedMessage id="contentAi.contentOrigin" defaultMessage="Origen del contenido:" />{' '}
          <strong>
            <FormattedMessage id="contentAi.fragment" defaultMessage="Fragmento" />{' '}
            <Link to={`/fragments/${fragment.id}`}>{fragment.title}</Link>
          </strong>
        </Box>
        <IfPermission permission={contentAi.contentAiPermissions.Contents_FragmentDelivery}>
          <Box mt={1}>
            <ContentAiFragmentDeliveryInfo fragment={fragment} content={content} />
          </Box>
        </IfPermission>
      </Box>
    </Alert>
  );
}
